import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardContent, CircularProgress, Box, Typography, Grid } from '@mui/material';
import { AgCharts } from 'ag-charts-community';
import { productHierarchyService } from '../../services/productHierarchy.service';

const HierarchyStatistics = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  
  // Chart references
  const levelChartRef = React.useRef(null);
  const departmentChartRef = React.useRef(null);
  
  useEffect(() => {
    const fetchStatistics = async () => {
      try {
        setLoading(true);
        const response = await productHierarchyService.getHierarchyStatistics();
        if (response.status === 'success' && response.data) {
          setStats(response.data);
          
          // Create level distribution chart
          if (response.data.levels && levelChartRef.current) {
            AgCharts.create({
              container: levelChartRef.current,
              title: {
                text: 'Hierarchy Level Distribution',
              },
              data: response.data.levels,
              series: [{
                type: 'pie',
                angleKey: 'node_count',
                labelKey: 'level_name',
                calloutLabelKey: 'level_name',
                sectorLabelKey: 'node_count',
                fills: ['#4472C4', '#ED7D31', '#A5A5A5'],
              }],
            });
          }
          
          // Create department distribution chart
          if (response.data.departments && departmentChartRef.current) {
            // Filter out departments with 0 products if needed
            const departmentData = response.data.departments
              .filter(dept => dept.product_count > 0)
              .sort((a, b) => b.product_count - a.product_count);
            
            if (departmentData.length > 0) {
              AgCharts.create({
                container: departmentChartRef.current,
                title: {
                  text: 'Product Distribution by Department',
                },
                data: departmentData,
                series: [{
                  type: 'column',
                  xKey: 'name',
                  yKey: 'product_count',
                  fill: '#5B9BD5',
                }],
              });
            }
          }
        } else {
          setError('Failed to load statistics data');
        }
      } catch (err) {
        console.error('Error fetching hierarchy statistics:', err);
        setError('An error occurred while fetching statistics');
      } finally {
        setLoading(false);
      }
    };
    
    fetchStatistics();
    
    // Cleanup function
    return () => {
      if (levelChartRef.current) {
        try {
          AgCharts.destroyChart({
            container: levelChartRef.current
          });
        } catch (e) {
          console.error('Error cleaning up level chart:', e);
        }
      }
      
      if (departmentChartRef.current) {
        try {
          AgCharts.destroyChart({
            container: departmentChartRef.current
          });
        } catch (e) {
          console.error('Error cleaning up department chart:', e);
        }
      }
    };
  }, []);
  
  return (
    <Card>
      <CardHeader title="Product Hierarchy Statistics" />
      <CardContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" align="center">{error}</Typography>
        ) : !stats ? (
          <Typography align="center">No statistics data available</Typography>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>Summary</Typography>
                  <Typography>Total Nodes: {
                    stats.levels ? stats.levels.reduce((sum, level) => sum + level.node_count, 0) : 'N/A'
                  }</Typography>
                  <Typography>Total Products: {stats.products?.total_products || 'N/A'}</Typography>
                  <Typography>Active Products: {stats.products?.active_products || 'N/A'}</Typography>
                  <Typography>Departments: {stats.departments?.length || 'N/A'}</Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <div ref={levelChartRef} style={{ width: '100%', height: '300px' }}></div>
            </Grid>
            
            <Grid item xs={12}>
              <div ref={departmentChartRef} style={{ width: '100%', height: '400px' }}></div>
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

export default HierarchyStatistics;